<div class="search-drawer" cdkTrapFocus>
  <div class="drawer-header">
    <div style="display: flex; gap: 5px;">
      <h2 translate data-automation-id="search-header-label">search.search</h2>
      <reader-ui-info-component [isLeftAligned]="true" instructionsFor="expansion_panel"/>
    </div>

    <button
      data-automation-id="search-drawer-close"
      placement="bottom"
      mhe-button
      [purpose]="ButtonPurpose.Icon"
      [title]="'shared.close' | translate"
      [mheTooltip]="'shared.close' | translate"
      (click)="closeEvent.emit()"
    >
      <i class="dpg-icon dpg-icon-system-close"></i>
    </button>

    <ng-container *ngIf="searching$ | async">
      <div class="searching-indicator">
        <mat-progress-bar class="rdrx-bar" data-automation-id="search-progress-bar" mode="query"></mat-progress-bar>
      </div>
    </ng-container>
  </div>

  <div class="drawer-body search-container">
    <mhe-input
      #searchInput
      class="search-input rdrx-hide-label"
      data-automation-id="search-input-box"
      icon="search"
      id="rdx-search-input"
      [disabled]="(ready$ | async) === false"
      [label]="searchLabel$ | async | translate"
      [placeholder]="searchLabel$ | async | translate"
      (keydown.arrowdown)="focusResults()"
      cdkFocusInitial
    ></mhe-input>

    <ng-container *ngIf="ready$ | async; else loading">
      <ng-container *ngIf="searchTerm$ | async as term">
        <ng-container *ngIf="(searching$ | async) === false">
          <ng-container *ngIf="results$ | async as results">
            <div *ngIf="results | searchPageNumberResult">
              <div data-automation-id="search-jump-to-page" class="jump-to-page">
                <a data-automation-id="search-jump-to-page-link" href="javascript:void(0)" (click)="selectResult(getPageNumberNode(results))">
                  <span>{{ 'search.jump_to_page' | translate }}</span>
                  <span>{{ (results | searchPageNumberResult).text }}</span>
                </a>
              </div>
            </div>

            <div class="results">
              <div class="result-count" data-automation-id="search-result-count">
                <ng-container *ngIf="results.length > 0; else refine">
                  <ng-container *ngIf="results.length <= 1000; else limitedResults" role="status">
                    {{ results.length }}
                    {{ (results.length === 1 ? 'search.result' : 'search.results') | translate | titlecase }}
                  </ng-container>
                </ng-container>
              </div>

              <ng-container *ngIf="(materialSearchClassicToggle$ | async) === true; else oldSearchTemplate">
                <ng-container *ngTemplateOutlet="newSearchTemplate"></ng-container>
              </ng-container>
            </div>
          </ng-container>
        </ng-container>
      </ng-container>
    </ng-container>
  </div>
</div>

<ng-template #loading>
  <div class="loading-container">
    <mat-progress-spinner
      class="rdrx-spinner"
      data-automation-id="search-loading-spinner"
      mode="determinate"
      [strokeWidth]="2"
      [diameter]="75"
      [value]="downloadPercent$ | async"
    ></mat-progress-spinner>

    <h3 class="loading-percent">{{ downloadPercent$ | async }}%</h3>
    <h3 class="loading-message">{{ 'search.loading_search' | translate }}</h3>

    <span translate class="sr-only" aria-live="polite">search.loading_search</span>
  </div>
</ng-template>

<ng-template #refine>
  <p>
    {{ 'search.no_results' | translate }}
  </p>
  <p>
    {{ 'search.refine_search' | translate }}
  </p>
</ng-template>

<ng-template #limitedResults>
  <ng-container>
    <span class="too-many"
      ><span class="sr-only">{{ 'assignment.warning_title' | translate }}</span>
      {{ 'search.limited_results' | translate }}</span
    >
  </ng-container>
</ng-template>

<ng-template #newSearchTemplate>
  <mat-accordion togglePosition="before" multi>
    <ng-container *ngFor="let group of groupedResults$ | async; let i = index">
      @if (!group.untitled) {
        <mat-expansion-panel
          hideToggle
          [expanded]="group.expanded"
          [class.expanded]="group.expanded"
          (closed)="group.expanded = false"
          (opened)="group.expanded = true"
        >
          <mat-expansion-panel-header
            #header
            data-automation-id="search-result-group-header"
            (keydown.arrowLeft)="group.expanded = false"
            (keydown.arrowRight)="group.expanded = true"
          >
            <span class="chevron-icon" [class.expanded]="group.expanded"></span>
            <h3>{{ group.name }}</h3>
          </mat-expansion-panel-header>

          <ul class="search-results-chapter">
            <li class="search-results-item" *ngFor="let item of group.results; let j = index">
              <a
                data-automation-id="search-result-item"
                #resultLink
                [attr.data-index]="j"
                [attr.data-group-index]="i"
                tabindex="0"
                href="javascript:void(0)"
                [innerHTML]="item | searchResult : searchTerm"
                (click)="selectResultItem(item)"
              ></a>
            </li>
          </ul>
        </mat-expansion-panel>
      }
      @else {
        <ul class="untitled-results" role="list" aria-label="Search results">
          @for (untitledItem of group.results; track untitledItem.id; let j = $index) {
            <li role="listitem" class="untitled-item">
              <a
                data-automation-id="search-result-unlisted-item"
                [attr.data-index]="j"
                [attr.data-group-index]="i"
                href="javascript:void(0)"
                tabindex="0"
                [innerHTML]="untitledItem | searchResult : searchTerm"
                (click)="selectResultItem(untitledItem)"
                (keydown.enter)="selectResultItem(untitledItem)"
                (keydown.space)="selectResultItem(untitledItem)"
              ></a>
            </li>
          }
        </ul>
      }
    </ng-container>
  </mat-accordion>
</ng-template>

<ng-template #oldSearchTemplate>
  <!-- result 'tree' -->
  <ngx-bonsai
    #searchBonsai
    class="rdrx"
    [nodesById]="tree$ | async"
    [rootNodeId]="rootNodeId"
    [expandedNodes]="expandedNodes$ | async"
    (expanderClick)="toggleNodeExpanded($event)"
  >
    <ng-template #treeNodeTemplate let-node>
      <!-- result -->
      <ng-container *ngIf="node.searchResult as result; else group">
        <a
          mheStopPropagation
          rdrxBonsaiNodeSelect
          data-automation-id="search-result-item"
          href="javascript:void(0)"
          class="rdrx-node-content ahe-ui-link"
          tabindex="-1"
          [innerHTML]="result | searchResult: term"
          (click)="selectResult(node)"
        ></a>
      </ng-container>
      <!-- group header -->
      <ng-template #group>
        <a
          mheStopPropagation
          rdrxBonsaiNodeSelect
          data-automation-id="search-result-group"
          href="javascript:void(0)"
          class="rdrx-node-content ahe-ui-link"
          tabindex="-1"
          [rdrxBonsaiNodeSelectKeyboard]="false"
          (click)="toggleNodeExpanded(node.id)"
        >
          {{ node.title }}
        </a>
      </ng-template>
    </ng-template>
  </ngx-bonsai>
</ng-template>
